import { escapedText, isRecord, list, plainText } from './payload';
import type {
  ChatTable,
  ChatTableCell,
  ChatTableColumn,
  ChatTableColumnType
} from './types';

const TABLE_COLUMN_TYPES: ChatTableColumnType[] = ['text', 'number'];

const TABLE_LIMITS = {
  titleChars: 80,
  labelChars: 40,
  cellChars: 80,
  minColumns: 2,
  maxColumns: 5,
  minRows: 2,
  maxRows: 30
} as const;

const toColumn = (value: unknown, index: number): ChatTableColumn => {
  if (!isRecord(value))
    throw new Error(`table columns[${index}] is not an object`);
  if (!TABLE_COLUMN_TYPES.includes(value.type as ChatTableColumnType)) {
    throw new Error(`table columns[${index}].type is unknown`);
  }
  return {
    label: escapedText(
      value.label,
      `columns[${index}].label`,
      TABLE_LIMITS.labelChars
    ),
    type: value.type as ChatTableColumnType
  };
};

const toCell = (
  value: unknown,
  type: ChatTableColumnType,
  name: string
): ChatTableCell => {
  if (value === null) return null;
  if (type === 'text') return escapedText(value, name, TABLE_LIMITS.cellChars);
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new Error(`table ${name} is not a number`);
  }
  return value;
};

export const parseChatTable = (payload: unknown): ChatTable => {
  if (!isRecord(payload)) throw new Error('table payload is not an object');

  const columns = list(
    payload.columns,
    'columns',
    TABLE_LIMITS.minColumns,
    TABLE_LIMITS.maxColumns
  ).map(toColumn);

  const rows = list(
    payload.rows,
    'rows',
    TABLE_LIMITS.minRows,
    TABLE_LIMITS.maxRows
  ).map((row, rowIndex) =>
    list(row, `rows[${rowIndex}]`, columns.length, columns.length).map(
      (value, columnIndex) =>
        toCell(
          value,
          columns[columnIndex]!.type,
          `rows[${rowIndex}][${columnIndex}]`
        )
    )
  );

  return {
    id: plainText(payload.id, 'id', TABLE_LIMITS.titleChars),
    title: escapedText(payload.title, 'title', TABLE_LIMITS.titleChars),
    columns,
    rows
  };
};
